import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Col } from 'react-flexbox-grid';
import moment from 'moment';
import { MenuItem, SelectField, Subheader, Paper, RaisedButton } from 'material-ui';
import { get, map } from 'lodash';

import Loading from 'components/Loading';
import PanelMessage from 'components/panels/PanelMessage';
import styles from 'styles/base';
import { fetchUserPaytickets } from './actions';
import { unitRender } from '../../utils/normalize';

const Paytickets = () => {
  const [payYear, setPayYear] = useState(new Date().getFullYear() - 1);
  const dispatch = useDispatch();

  const { payticketReducer, languageReducer } = useSelector(state => state);
  const {
    paytickets, payYearOptions, hasError, error, loading,
  } = payticketReducer;
  const { language } = languageReducer;
  moment.locale(language);

  useEffect(() => {
    const lastYear = new Date().getFullYear() - 1;
    dispatch(fetchUserPaytickets(lastYear));
  }, [dispatch]);

  const fetchTicket = (year) => {
    setPayYear(year);
    dispatch(fetchUserPaytickets(year));
  };

  const setMargin = (row, i) => ({ marginBottom: row.length - 1 !== i ? 8 : 0 });

  return (
    <Col xs={12} sm={12} md={8} mdOffset={2} style={{ paddingTop: 0, paddingBottom: 16 }}>
      <Subheader style={styles.subheader}><FormattedMessage id="payticket.title" /></Subheader>
      <SelectField
        style={{ paddingLeft: 16 }}
        className="no-print"
        floatingLabelText={<FormattedMessage id="payticket.select" />}
        value={payYear}
        onChange={(a, b, value) => fetchTicket(value)}
      >
        {payYearOptions.map(y => <MenuItem key={y} value={y} primaryText={y} />)}
      </SelectField>

      {loading && <Loading height="70vh" />}
      {!loading && !hasError && paytickets.length === 0 && (
        <PanelMessage message={<FormattedMessage id="payticket.empty" />} type="info" iconName="info" />
      )}
      {!loading && hasError && (
        <Col xs={12} sm={12} md={8} mdOffset={2} style={{ paddingTop: 0, paddingBottom: 16 }}>
          <PanelMessage
            type="error"
            iconName="error"
            message={error && error.Message ? error.Message : <FormattedMessage id="payticket.error" />}
          />
        </Col>
      )}

      <div style={{ maxWidth: 960, margin: 'auto' }}>
        {!loading && paytickets.length > 0 && (
          map(paytickets, ({
            firstRow,
            restRows,
            EmployeeName,
            Address,
            PostCode,
            PostCodeName,
            EntityNR,
            Header,
          }, i) => (
            <Paper key={i} style={{ marginTop: 24 }}>
              <div className="pay-ticket-table-container">
                <div className="pay-ticket-table-header">
                  <FormattedMessage id="payticket.ticket" />
                  {' '}
                  {payYear}
                </div>
                <table className="pay-ticket-table">
                  <tbody>
                    <tr style={{ backgroundColor: '#F5F5F5' }}>
                      <th style={{ textAlign: 'center', width: 120 }}><FormattedMessage id="payticket.column" /></th>
                      <th style={{ textAlign: 'left', width: 300 }}><FormattedMessage id="payticket.name" /></th>
                      <th style={{ textAlign: 'right', minWidth: 100 }}><FormattedMessage id="payticket.total" /></th>
                      <th style={{ textAlign: 'center', minWidth: 100 }}><FormattedMessage id="payticket.field" /></th>
                      <th style={{ textAlign: 'right', paddingRight: 36 }}><FormattedMessage id="payticket.totalField" /></th>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'center' }}>
                        {map(firstRow, (col, index) => (
                          <div key={index} style={setMargin(firstRow, index)}>
                            {get(col, 'PaySalaryID')}
                          </div>
                        ))}
                      </td>
                      <td>
                        {map(firstRow, (col, index) => (
                          <div key={index} style={setMargin(firstRow, index)}>
                            {get(col, 'PaySalaryName')}
                          </div>
                        ))}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {map(firstRow, (col, index) => (
                          <div key={index} style={setMargin(firstRow, index)}>
                            {`${unitRender(get(col, 'Samtals'), 0)} kr.`}
                          </div>
                        ))}
                      </td>
                      <td />
                      <td />
                    </tr>
                    {map(restRows, (row, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: 'center' }}>
                          {get(row, 'PaySalaryID')}
                        </td>
                        <td>
                          {get(row, 'PaySalaryName')}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {get(row, 'Samtals') && `${unitRender(get(row, 'Samtals'), 0)} kr.`}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {get(row, 'SkNr')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 900, paddingRight: 36 }}>
                          {get(row, 'TotalPayField') && `${unitRender(get(row, 'TotalPayField'), 0)} kr.`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pay-ticket-table-footer">
                  <div>
                    <div>{EmployeeName}</div>
                    <div>{Address}</div>
                    <div>{`${PostCode} ${PostCodeName}`}</div>
                    <div>{EntityNR}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div />
                    <div>{get(Header, 'OrgCompanyName')}</div>
                    <div>{get(Header, 'Address')}</div>
                    <div>{`${get(Header, 'PostCode')} ${get(Header, 'PostCodeName')}`}</div>
                  </div>
                </div>
              </div>
            </Paper>
          ))
        )}
        <div
          style={{
            marginTop: 18,
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <div />
          {!loading && paytickets.length > 0 && <RaisedButton
            label={<FormattedMessage id="print" />}
            primary
            onClick={() => window.print()}
            className="no-print"
          />}
        </div>
      </div>
    </Col>
  );
};

export default Paytickets;



// WEBPACK FOOTER //
// ./src/sites/payTickets/Paytickets.js